/** The presigned S3 POST the API hands back: an endpoint plus the form fields to send with it. */
export interface PresignedPost {
	url: string;
	fields: ReadonlyArray<{ name: string; value: string }>;
}

/**
 * Uploads a file to a presigned S3 POST endpoint.
 *
 * The signed policy carries the size cap, so an oversized file is rejected here by S3 rather than
 * after the fact. The fields must be appended before the file part — S3 ignores anything after it.
 */
export async function uploadToPresignedPost(
	upload: PresignedPost,
	file: File,
): Promise<void> {
	const formData = new FormData();
	for (const field of upload.fields) {
		formData.append(field.name, field.value);
	}
	formData.append("file", file);

	let response: Response;
	try {
		response = await fetch(upload.url, { method: "POST", body: formData });
	} catch (cause) {
		// A cross-origin failure rejects before any Response exists, so there is no status and
		// no body to report — the browser deliberately hides both. In practice it means the
		// bucket's CORS rules do not list this origin (local dev is not an allowed origin on
		// the deployed buckets), and it also masks whatever status S3 actually returned.
		throw new Error(
			`Upload of "${file.name}" was blocked by the browser before a response could be read. ` +
				`The storage bucket at ${new URL(upload.url).host} does not allow uploads from ` +
				`${globalThis.location?.origin ?? "this origin"}.`,
			{ cause },
		);
	}

	if (!response.ok) {
		// S3 answers with an XML body whose <Message> says what actually went wrong
		// (wrong bucket, expired signature, policy mismatch). Without it every failure
		// looks like a bare status code and is undebuggable from the UI.
		throw new Error(
			`Upload of "${file.name}" failed (${response.status}): ${await s3ErrorMessage(response)}`,
		);
	}
}

async function s3ErrorMessage(response: Response): Promise<string> {
	try {
		const body = await response.text();
		const message =
			body.match(/<Message>([\s\S]*?)<\/Message>/)?.[1] ??
			body.slice(0, 200).trim();
		return message || response.statusText;
	} catch {
		return response.statusText;
	}
}
